/* Дашборд (DSH) — как в прототипе (index.html): «Первые шаги» нового тренера, четыре карточки —
   не счётчики, а ответ на вопрос «что мне сейчас делать» (каждая ведёт туда, где с этим работают),
   и одна лента событий по дням с фильтром по типу; ответ на вопрос — прямо в ленте. */
import { trpc } from '@tg/app-core';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { hasProg, lastActive, useClients, useMe, type DashData } from '../lib/data';
import { limitLabel, planOf } from '../lib/dict';
import { addDays, daysBetween, dm, humanDate, plural, today } from '../lib/format';
import { DatePicker } from '../calendar/Calendar';
import { usePageTitle } from '../shell/Shell';
import { evKey, FeedList, TYPES, useEvents, type Ev, type EvType } from '../ui/Feed';
import { ICON } from '../ui/icons';
import { LimitAlert } from '../ui/LimitAlert';
import { TodayModal } from '../ui/TodayModal';

type Onb = NonNullable<DashData['onb']>;
type StepKey = 'cal' | 'compose' | 'pm' | 'invite';

interface Kpi {
  k: string;
  ic: ReactNode;
  v: ReactNode;
  d?: string;
  href?: string;
  feed?: EvType;
  /** Клик открывает окно (а не ссылку или фильтр ленты) */
  open?: () => void;
  bar?: number;
  rows?: [string, number][];
  acc?: boolean;
  warn?: boolean;
}

/* ─── «Первые шаги» ─── */

const STEPS: { k: StepKey; n: string; btn: string; href: (o: Onb) => string }[] = [
  { k: 'cal', n: 'Найдите в «Календаре» демо-тренировку и добавьте в неё новое упражнение', btn: 'В календарь', href: (o) => '/calendar?client=' + o.demoId },
  { k: 'compose', n: 'Создайте новую тренировку для демо-клиента', btn: 'Создать', href: (o) => `/constructor?client=${o.demoId}&date=${o.composeDate}` },
  { k: 'pm', n: 'Найдите в «Клиентах» демо-клиента и добавьте ему новый рекорд', btn: 'К клиентам', href: () => '/clients' },
  { k: 'invite', n: 'Пригласите нового клиента по ссылке', btn: 'Где взять ссылку', href: () => '/clients?onb=invite' },
];

/* Анимация как у чек-листа HWPO: при входе на дашборд карточка проявляется, шаги выезжают по одному,
   полоса дорастает до своего значения; шаг, выполненный с прошлого визита, «щёлкает» галочкой. */
function Onboarding({ o }: { o: Onb }) {
  const utils = trpc.useUtils();
  const set = trpc.trainer.onbSet.useMutation();
  const [fold, setFold] = useState(o.fold);
  const [hidden, setHidden] = useState(false);
  /* Какие шаги тренер уже видел выполненными — на момент открытия страницы */
  const [shown] = useState(o.shown);
  const [enter, setEnter] = useState(true);
  const [w, setW] = useState(0);
  const done = STEPS.filter((x) => o.steps[x.k]);
  const n = done.length;
  const all = n === STEPS.length;
  const pct = (n / STEPS.length) * 100;

  useLayoutEffect(() => {
    /* Полоса дорастает со следующего кадра, чтобы сработал transition */
    const r = requestAnimationFrame(() => requestAnimationFrame(() => setW(pct)));
    return () => cancelAnimationFrame(r);
  }, [pct]);
  useEffect(() => {
    const t = setTimeout(() => setEnter(false), 1400);
    return () => clearTimeout(t);
  }, []);
  /* Выполненные шаги запоминаем как увиденные — и когда дашборд догрузил свежие данные, — чтобы «щелчок» был один раз */
  const doneKeys = done.map((x) => x.k).join(',');
  useEffect(() => {
    const keys = doneKeys ? doneKeys.split(',') : [];
    if (!keys.some((k) => !o.shown.includes(k))) return;
    set.mutate({ shown: keys });
    /* и в кеше — следующий заход на дашборд уже не «щёлкает» эти шаги заново */
    utils.trainer.dashboard.setData(undefined, (d) => (d?.onb ? { ...d, onb: { ...d.onb, shown: keys } } : d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doneKeys]);

  if (hidden) return null;
  const f = fold && !all;
  return (
    <div className="onbw">
      <section className={'onb' + (all ? ' fin' : '') + (f ? ' fold' : '') + (enter ? ' enter' : '')} id="onb">
        <div className="oh">
          <span className="k">
            <ICON.star /> {all ? 'Все шаги пройдены' : 'Первые шаги'}
          </span>
          <span className="on">
            {n} / {STEPS.length}
          </span>
          <button
            className="ol"
            onClick={() => {
              if (all) {
                setHidden(true);
                set.mutate({ hidden: true }, { onSettled: () => utils.trainer.dashboard.invalidate() });
              } else {
                setFold(!f);
                set.mutate({ fold: !f });
              }
            }}
          >
            {all ? 'Убрать' : f ? 'Показать' : 'Свернуть'}
          </button>
        </div>
        <div className="obar">
          <i style={{ width: w + '%' }} />
        </div>
        <div className="obody">
          <ol className="osteps">
            {STEPS.map((x, i) => {
              const ok = o.steps[x.k];
              return (
                <li key={x.k} className={'st' + (ok ? ' done' : '') + (ok && !shown.includes(x.k) ? ' just' : '')} style={{ ['--i' as string]: i }}>
                  <span className="ck">{ok && <ICON.chk />}</span>
                  <span className="t">
                    {x.n}
                    {!ok && (
                      <>
                        {' '}
                        <Link className="ol" to={x.href(o)}>
                          {x.btn}
                        </Link>
                      </>
                    )}
                  </span>
                </li>
              );
            })}
          </ol>
        </div>
      </section>
    </div>
  );
}

/* ─── «(N новых)» у заголовка ленты: события, которых тренер ещё не видел на дашборде.
   Считается один раз при открытии страницы; список виденных — на этом устройстве ─── */
function useFeedNew(ev: Ev[], ready: boolean, tid: string | undefined) {
  const [n, setN] = useState<number | null>(null);
  useEffect(() => {
    if (!ready || !tid || n !== null) return;
    const key = 'tg.feedSeen.' + tid;
    let seen = new Set<string>();
    try {
      seen = new Set(JSON.parse(localStorage.getItem(key) || '[]'));
    } catch {
      /* хранилище недоступно — все события новые */
    }
    const keys = ev.map(evKey);
    setN(keys.filter((k) => !seen.has(k)).length);
    try {
      localStorage.setItem(key, JSON.stringify(keys));
    } catch {
      /* не запомнили — в следующий раз снова новые */
    }
  }, [ready, tid, ev, n]);
  return n;
}

export function Dashboard() {
  usePageTitle('Дашборд');
  /* Дашборд — сводка того, что сделано в других разделах: при каждом открытии берём свежее,
     иначе «Первые шаги» и карточки отмечаются только после перезагрузки страницы */
  const dash = trpc.trainer.dashboard.useQuery(undefined, { refetchOnMount: 'always', refetchOnWindowFocus: true });
  const feed = trpc.trainer.feed.useQuery(undefined, { refetchOnMount: 'always', refetchOnWindowFocus: true });
  const me = useMe().data;
  const cs = useClients().data;
  const ev = useEvents(feed.data);
  /* Фильтр ленты (прототип index.html): типы — несколько сразу (пусто — все), период — быстрые варианты или свой «с … по …» */
  const [types, setTypes] = useState<Set<EvType>>(new Set());
  const [per, setPer] = useState<Per>({ k: 'all', from: null, to: null });
  const nw = useFeedNew(ev, !!feed.data, me?.id);
  const feedH = useRef<HTMLDivElement>(null);
  const s = dash.data?.stats;
  const [todayOpen, setTodayOpen] = useState(false);
  const T = dash.data?.today || today();

  /* Нужно внимание: программа на исходе (написано не дальше недели) и клиенты без тренировок 3+ дня */
  const ending = dash.data ? dash.data.queue.filter((x) => x.till).length : 0;
  const idle = (cs || []).filter((c) => {
    if (c.frozenAt || !hasProg(c)) return false;
    const l = lastActive(c);
    return !!l && daysBetween(l, T) >= 3;
  }).length;
  const att = ending + idle;
  const sub = me?.sub;
  const pl = planOf(sub?.trial ? sub.next || 'c5' : me?.plan);
  const limit = me?.clientLimit ?? pl.limit;
  const used = me?.used ?? 0;
  const until = me?.planUntil ? humanDate(me.planUntil) : '';
  const acc = !sub || !until
    ? ''
    : sub.ended
      ? (sub.trial ? 'пробный период закончился ' : 'подписка закончилась ') + until
      : sub.trial
        ? 'пробный период до ' + until
        : sub.canceled
          ? 'подписка отменена, доступ до ' + until
          : 'подписка до ' + until;
  const st: Kpi[] = s
    ? [
        {
          k: 'Тренировки сегодня',
          ic: <ICON.cal />,
          /* Есть назначенные — список клиентов со статусом; нет — календарь, как раньше */
          ...(s.today ? { open: () => setTodayOpen(true) } : { href: '/calendar' }),
          v: s.today ? `${s.todayDone} из ${s.today}` : '0',
          bar: s.today ? s.todayDone / s.today : undefined,
          d: s.today ? 'выполнено из назначенных на сегодня' : 'на сегодня ничего не назначено',
        },
        {
          k: 'Нужно внимание',
          ic: <ICON.prog />,
          href: '/clients',
          v: att,
          warn: att > 0,
          rows: att
            ? [
                ['Тренировки на исходе', ending],
                ['Нет тренировок 3+ дня', idle],
              ]
            : undefined,
          d: att ? undefined : 'все клиенты в графике',
        },
        { k: 'Ждут ответа', ic: <ICON.chat />, feed: 'q', v: s.unanswered, acc: s.unanswered > 0, d: s.unanswered ? 'вопросов клиентов — показать в ленте' : 'вопросов без ответа нет' },
        {
          k: 'Аккаунт',
          ic: <ICON.users />,
          href: '/profile#billing',
          v: `${used} из ${limitLabel(limit)}`,
          warn: used >= limit * 0.9,
          d: (sub?.trial && !sub.ended ? 'бесплатно' : `${pl.price.toLocaleString('ru-RU')} ₽ в месяц`) + (acc ? ' · ' + acc : ''),
        },
      ]
    : [];
  const inRange = (e: Ev) => (!per.from || e.d >= per.from) && (!per.to || e.d <= per.to);
  const list = ev.filter((e) => (!types.size || types.has(e.t)) && inRange(e));

  const kpi = (x: Kpi) => {
    const body = (
      <>
        <span className="k">
          {x.ic} {x.k}
        </span>
        <div className={'v ' + (x.acc ? 'acc' : x.warn ? 'warn' : '')}>{x.v}</div>
        {x.bar != null && (
          <div className="kbar">
            <i style={{ width: Math.round(x.bar * 100) + '%' }} />
          </div>
        )}
        {x.d && <div className="d">{x.d}</div>}
        {x.rows && (
          <div className="rows">
            {x.rows.map(([t, n]) => (
              <div key={t}>
                <span>{t}</span>
                <b>{n}</b>
              </div>
            ))}
          </div>
        )}
      </>
    );
    if (x.open)
      return (
        <button key={x.k} type="button" className="kpi lnk" onClick={x.open}>
          {body}
        </button>
      );
    if (x.href)
      return (
        <Link key={x.k} className="kpi lnk" to={x.href}>
          {body}
        </Link>
      );
    const on = types.size === 1 && types.has(x.feed as EvType);
    return (
      <button
        key={x.k}
        type="button"
        className={'kpi lnk' + (on ? ' on' : '')}
        onClick={() => {
          setTypes(new Set(on ? [] : [x.feed as EvType]));
          if (!on) feedH.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      >
        {body}
      </button>
    );
  };

  return (
    <div className="pagewrap wide" style={{ paddingTop: 14 }}>
      <section className="pane" id="page">
        <LimitAlert />
        {dash.data?.onb && <Onboarding o={dash.data.onb} />}
        <div className="dkpis">{st.map(kpi)}</div>
        <div className="feedh" ref={feedH}>
          <h2>
            Лента событий{' '}
            {nw !== null && <span className="fnew">({nw ? nw + ' ' + plural(nw, 'новое', 'новых', 'новых') : 'новых событий нет'})</span>}
          </h2>
        </div>
        <FeedFilter ev={ev} types={types} setTypes={setTypes} per={per} setPer={setPer} inRange={inRange} />
        <div className="feed1">{feed.data && <FeedList list={list} />}</div>
      </section>
      {todayOpen && <TodayModal onClose={() => setTodayOpen(false)} />}
    </div>
  );
}

/* ─── Фильтр ленты: два раскрывающихся списка и «Сбросить» (прототип index.html) ─── */
type Per = { k: 'all' | 'today' | 'yest' | '7' | '30' | 'custom'; from: string | null; to: string | null };
const PERIODS: [Per['k'], string][] = [
  ['all', 'За всё время'],
  ['today', 'Сегодня'],
  ['yest', 'Вчера'],
  ['7', '7 дней'],
  ['30', '30 дней'],
];
const perRange = (k: Per['k'], T: string): [string | null, string | null] =>
  k === 'today' ? [T, T] : k === 'yest' ? [addDays(T, -1), addDays(T, -1)] : k === '7' ? [addDays(T, -6), T] : k === '30' ? [addDays(T, -29), T] : [null, null];
const DDCHEV = (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 6.5l4 4 4-4" />
  </svg>
);

function FeedFilter({ ev, types, setTypes, per, setPer, inRange }: { ev: Ev[]; types: Set<EvType>; setTypes: (s: Set<EvType>) => void; per: Per; setPer: (p: Per) => void; inRange: (e: Ev) => boolean }) {
  const [open, setOpen] = useState<'' | 'T' | 'D'>('');
  const [pick, setPick] = useState<{ end: 'from' | 'to'; el: HTMLElement } | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const T = today();
  /* Закрывается по клику мимо и Esc; календарик выбора даты — не «мимо» */
  useEffect(() => {
    if (!open) return;
    const out = (e: MouseEvent) => !(e.target as Element).closest?.('.fpop, .fddb, .dpick') && (setOpen(''), setPick(null));
    const key = (e: KeyboardEvent) => e.key === 'Escape' && (setOpen(''), setPick(null));
    document.addEventListener('click', out, true);
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('click', out, true);
      document.removeEventListener('keydown', key, true);
    };
  }, [open]);

  const ts = (Object.keys(TYPES) as EvType[]).filter((t) => ev.some((e) => e.t === t));
  const typeLabel = !types.size ? 'Все события' : types.size === 1 ? TYPES[[...types][0]] : TYPES[[...types][0]] + ' и ещё ' + (types.size - 1);
  const perLabel =
    per.k !== 'custom'
      ? PERIODS.find((p) => p[0] === per.k)![1]
      : per.from && per.to && per.from === per.to
        ? humanDate(per.from)
        : [per.from ? 'с ' + dm(per.from) : '', per.to ? 'по ' + dm(per.to) : ''].filter(Boolean).join(' ');
  const toggle = (t: EvType) => {
    const n = new Set(types);
    if (n.has(t)) n.delete(t);
    else n.add(t);
    setTypes(n);
  };
  const setEnd = (end: 'from' | 'to', d: string) => {
    let from = per.k === 'custom' ? per.from : null;
    let to = per.k === 'custom' ? per.to : null;
    if (end === 'from') from = d;
    else to = d;
    if (from && to && from > to) [from, to] = [to, from];
    setPer({ k: 'custom', from, to });
  };

  return (
    <div className="ffl" ref={box}>
      <div className="fdd">
        <button className={'fddb' + (types.size ? ' on' : '')} aria-haspopup="true" aria-expanded={open === 'T'} onClick={() => setOpen(open === 'T' ? '' : 'T')}>
          <span>{typeLabel}</span>
          {DDCHEV}
        </button>
        {open === 'T' && (
          /* Список остаётся открытым — выбирают несколько типов подряд; лента обновляется сразу */
          <div className="fpop">
            <label className="fopt all">
              <input type="checkbox" checked={!types.size} onChange={() => setTypes(new Set())} />
              <span>Все события</span>
              <s>{ev.filter(inRange).length}</s>
            </label>
            {ts.map((t) => (
              <label className="fopt" key={t}>
                <input type="checkbox" checked={types.has(t)} onChange={() => toggle(t)} />
                <span>{TYPES[t]}</span>
                <s>{ev.filter((e) => e.t === t && inRange(e)).length}</s>
              </label>
            ))}
          </div>
        )}
      </div>
      <div className="fdd">
        <button className={'fddb' + (per.k !== 'all' ? ' on' : '')} aria-haspopup="true" aria-expanded={open === 'D'} onClick={() => setOpen(open === 'D' ? '' : 'D')}>
          <span>{perLabel || 'Период'}</span>
          {DDCHEV}
        </button>
        {open === 'D' && (
          <div className="fpop">
            {PERIODS.map(([k, t]) => (
              <button
                key={k}
                className={'fopt' + (per.k === k ? ' sel' : '')}
                onClick={() => {
                  const [from, to] = perRange(k, T);
                  setPer({ k, from, to });
                  setOpen('');
                }}
              >
                <span>{t}</span>
              </button>
            ))}
            <div className="frng">
              <span>Период</span>
              {(['from', 'to'] as const).map((end) => (
                <button key={end} className="fdt" onClick={(e) => setPick({ end, el: e.currentTarget })}>
                  {per.k === 'custom' && per[end] ? dm(per[end]!) : end === 'from' ? 'с …' : 'по …'}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {(types.size > 0 || per.k !== 'all') && (
        <button
          className="fclr"
          onClick={() => {
            setTypes(new Set());
            setPer({ k: 'all', from: null, to: null });
          }}
        >
          Сбросить
        </button>
      )}
      {pick && (
        <DatePicker
          anchor={pick.el}
          value={(per.k === 'custom' && per[pick.end]) || T}
          onPick={(d) => {
            setEnd(pick.end, d);
            setPick(null);
          }}
          onClose={() => setPick(null)}
        />
      )}
    </div>
  );
}
